import { Sentry } from 'lib-shared-sentry';
import type { AnyAction, Dispatch } from 'redux';

import { notificationActions } from '../../../../../components/notifications/actions/notification';
import type { SagaSettlement, TwilioTokenRegion } from '../types';

import type {
  AsyncTwilioCommandOptions,
  RegionAwareTwilioCommandOptions,
} from './types';
import { abortTwilioCommandSymbol } from './types';

const { notificationAdd } = notificationActions;

/** Used as a default when an action doesn't carry resolve/reject callbacks. */
const noop = () => {};
const missingCsidReason = 'missing_csid' as const;

type MissingCsidFailurePayload = {
  reason: typeof missingCsidReason;
};

/**
 * Sends an error to Sentry, but swallows any failure that Sentry itself throws
 * so a broken telemetry connection can never bring down a Twilio command.
 */
function captureExceptionSafely(error: unknown): void {
  try {
    Sentry.captureException(error);
  } catch {
    // Sentry itself should not be able to break command handling.
  }
}

/**
 * Returns the resolve/reject callbacks the saga attached to this action. Falls
 * back to no-ops so callers never have to null-check before settling.
 */
function getActionPromiseCallbacks(action: AnyAction): SagaSettlement {
  return {
    resolve: action.meta?.resolve ?? noop,
    reject: action.meta?.reject ?? noop,
  };
}

/**
 * Calls `fn` in a try/catch so that side-effect callbacks (notifications, Redux
 * dispatches, etc.) can never throw past promise settlement or leave an
 * unhandled rejection. Any error they produce goes to Sentry instead.
 */
function runSafely(fn: () => void): void {
  try {
    fn();
  } catch (error) {
    captureExceptionSafely(error);
  }
}

type ExecuteTwilioCommand = <TResult>(
  options: AsyncTwilioCommandOptions<TResult>,
) => void;

type ExecuteRegionAwareTwilioCommand = <TResult>(
  options: RegionAwareTwilioCommandOptions<TResult>,
) => void;

type ReadCsidFromAction = (params: {
  action: AnyAction;
  context: string;
  onRejected?: (payload: MissingCsidFailurePayload) => void;
}) => string | number | undefined;

export type TwilioCommandExecutor = {
  executeTwilioCommand: ExecuteTwilioCommand;
  executeRegionAwareTwilioCommand: ExecuteRegionAwareTwilioCommand;
  readCsidFromAction: ReadCsidFromAction;
};

/**
 * Creates the set of helpers used to run Twilio commands. Handles promise
 * settlement, region validation, user-facing errors, and Sentry reporting in
 * one consistent place so individual action handlers don't have to.
 */
export function createTwilioCommandExecutor(deps: {
  dispatch: Dispatch<AnyAction>;
  ensureDeviceRegion: (region: TwilioTokenRegion) => Promise<void>;
}): TwilioCommandExecutor {
  const { dispatch, ensureDeviceRegion } = deps;

  /**
   * Shows a "Telephony issue" notification banner to the agent and also logs
   * the message to Sentry so we have a server-side record of internal
   * failures.
   */
  const notifyInternalIssue = (message: string): void => {
    dispatch(
      notificationAdd({
        title: 'Telephony issue',
        text: message,
        className: 'danger',
        icon: 'announcement-slash',
      }),
    );
    Sentry.captureMessage(message, 'error');
  };

  /**
   * Called when a command action is missing a `csid` in its payload. Resolves
   * the saga promise so it doesn't hang, then reports the bug via console, the
   * notification banner, and the optional `onRejected` callback.
   */
  const rejectMissingCsid = ({
    action,
    context,
    onRejected,
  }: {
    action: AnyAction;
    context: string;
    onRejected?: (payload: MissingCsidFailurePayload) => void;
  }): void => {
    const message = `Twilio: ${context} was not given any csid in payload params.`;

    getActionPromiseCallbacks(action).resolve(undefined);

    runSafely(() => {
      console.error(message);
      notifyInternalIssue(message);
      onRejected?.({ reason: missingCsidReason });
    });
  };

  /**
   * Called when a token region couldn't be determined before starting a call.
   * Resolves the saga promise so it doesn't hang and shows the agent an
   * actionable error message.
   */
  const rejectMissingTokenRegion = ({
    action,
    onFailed,
  }: {
    action: AnyAction;
    onFailed: () => void;
  }): void => {
    const message =
      'Could not start the call because the phone number region is unavailable. ' +
      'Refresh and try again, or use a different phone number. If the problem persists, contact support.';

    getActionPromiseCallbacks(action).resolve(undefined);

    runSafely(() => {
      notifyInternalIssue(message);
      onFailed();
    });
  };

  /**
   * Runs a Twilio command and guarantees the saga promise is always settled.
   *
   * Key behaviours to be aware of:
   *
   * - The promise always **resolves** (never rejects), so callers don't need
   *   `.catch()`. Errors are surfaced through Redux (`*Failed` actions), the
   *   notification banner, and Sentry instead.
   * - `onSucceeded` and `onFailed` run inside `runSafely`, meaning a crash inside
   *   them cannot prevent the promise from settling or produce an unhandled
   *   rejection.
   * - Throwing `abortTwilioCommandSymbol` from `run()` is an escape hatch for
   *   pre-flight failures (e.g. missing region) that have already settled the
   *   promise themselves. When this symbol is thrown, neither `onSucceeded` nor
   *   `onFailed` are called and nothing else is settled.
   */
  const executeTwilioCommand = <TResult>({
    action,
    run,
    onSucceeded,
    onFailed,
    captureException = true,
  }: AsyncTwilioCommandOptions<TResult>): void => {
    const actionCallbacks = getActionPromiseCallbacks(action);

    /**
     * Inner async wrapper so we can use try/catch with await while keeping the
     * outer `executeTwilioCommand` synchronous.
     */
    async function executeCommand() {
      let result: TResult;

      try {
        result = await run();
      } catch (error) {
        if (error === abortTwilioCommandSymbol) {
          return;
        }

        actionCallbacks.resolve(undefined);
        runSafely(() => onFailed(error));

        if (captureException) {
          captureExceptionSafely(error);
        }
        return;
      }

      actionCallbacks.resolve(result);
      runSafely(() => onSucceeded(result));
    }

    executeCommand();
  };

  /**
   * Looks up the token region for the current call. If one can't be found, it
   * handles the failure (resolves the saga promise, shows an error) and then
   * throws `abortTwilioCommandSymbol` to stop the rest of the command.
   */
  const requireTokenRegion = async ({
    action,
    resolveRegion,
    onMissingRegion,
  }: {
    action: AnyAction;
    resolveRegion: () => Promise<TwilioTokenRegion | undefined>;
    onMissingRegion: () => void;
  }): Promise<TwilioTokenRegion> => {
    const tokenRegion = await resolveRegion();

    if (tokenRegion != null) {
      return tokenRegion;
    }

    rejectMissingTokenRegion({
      action,
      onFailed: onMissingRegion,
    });
    throw abortTwilioCommandSymbol;
  };

  /**
   * Like `executeTwilioCommand`, but also handles region-aware setup before
   * running the command: resolves the token region, calls any pre-run hook, and
   * makes sure the Twilio device is configured for that region.
   */
  const executeRegionAwareTwilioCommand = <TResult>({
    action,
    resolveRegion,
    onMissingRegion,
    beforeRun,
    run,
    onSucceeded,
    onFailed,
    captureException,
  }: RegionAwareTwilioCommandOptions<TResult>): void => {
    executeTwilioCommand({
      action,
      run: async () => {
        const tokenRegion = await requireTokenRegion({
          action,
          resolveRegion,
          onMissingRegion,
        });
        beforeRun?.();
        await ensureDeviceRegion(tokenRegion);
        return run();
      },
      onSucceeded,
      onFailed,
      captureException,
    });
  };

  /**
   * Pulls the `csid` out of the action payload. If it's missing, kicks off the
   * standard rejection flow and returns `undefined` so the caller knows to stop
   * processing.
   */
  const readCsidFromAction = ({
    action,
    context,
    onRejected,
  }: {
    action: AnyAction;
    context: string;
    onRejected?: (payload: MissingCsidFailurePayload) => void;
  }): string | number | undefined => {
    const csid = action.payload.csid;

    if (csid != null) {
      return csid as string | number;
    }

    rejectMissingCsid({
      action,
      context,
      onRejected,
    });
    return undefined;
  };

  return {
    executeTwilioCommand,
    executeRegionAwareTwilioCommand,
    readCsidFromAction,
  };
}
